import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";

import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CategoryPicker } from "@/components/portal/lms/CategoryPicker";
import { CuratorGate } from "@/components/portal/lms/CuratorGate";
import { AssignTestDialog } from "@/components/portal/lms/AssignTestDialog";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useLmsCuratorAccess } from "@/hooks/useLmsCuratorAccess";
import type { CategoryHierarchyNode } from "@/services/api/categories";
import { lmsApi, type LmsTest } from "@/services/api/lms";

export const Route = createFileRoute("/portal/lms/assign")({
  component: LmsAssignPage,
});

/**
 * Assign published tests to learners (`/portal/lms/assign`, Wave C D9). Pick a
 * category the caller curates, then a published test in it, then the learner
 * and team from CP pickers. The picker lists only categories
 * `useLmsCuratorAccess` admits; dp's per-category curator gate is the real one.
 */
function LmsAssignPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<LmsTest | null>(null);
  const access = useLmsCuratorAccess();
  const { canCurate } = access;
  const curatorFilter = useCallback((node: CategoryHierarchyNode) => canCurate(node.id), [canCurate]);

  const tests = useQuery({
    queryKey: ["lms", "tests", categoryId, "published"],
    queryFn: () =>
      lmsApi.listTests(token ?? "", {
        topicCategoryId: categoryId as string,
        isPublished: true,
        pageSize: 200,
      }),
    enabled: !!token && !!categoryId,
  });

  const items = tests.data?.items ?? [];

  return (
    <div className="space-y-4" data-testid="lms-assign-page">
      <Helmet>
        <title>Assign tests | LMS</title>
      </Helmet>
      <PageHeader
        title="Assign tests"
        description="Pick a category and a published test, then assign it to a learner and their team."
      />

      <CuratorGate access={access}>
        <CategoryPicker
          token={token ?? ""}
          orgId={orgId}
          value={categoryId}
          onChange={setCategoryId}
          filter={curatorFilter}
          emptyMessage="You don't curate any categories."
        />

        {!categoryId ? (
          <p className="text-sm text-muted-foreground">Select a category to see its published tests.</p>
        ) : tests.isLoading ? (
          <div className="flex justify-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : tests.error ? (
          <p className="text-sm text-destructive" data-testid="lms-assign-tests-error">
            {tests.error instanceof Error ? tests.error.message : "Failed to load tests."}
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="lms-assign-tests-empty">
            No published tests in this category.
          </p>
        ) : (
          <Table data-testid="lms-assign-tests">
            <TableHeader>
              <TableRow>
                <TableHead>Test</TableHead>
                <TableHead className="text-right">Questions</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Pass mark</TableHead>
                <TableHead className="w-[110px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((test) => (
                <TableRow key={test.id} data-testid="lms-assign-test-row">
                  <TableCell>
                    <div className="font-medium">{test.title}</div>
                    {test.description && <div className="text-xs text-muted-foreground">{test.description}</div>}
                  </TableCell>
                  <TableCell className="text-right">{test.question_count}</TableCell>
                  <TableCell className="text-right">{Math.round(test.duration_seconds / 60)} min</TableCell>
                  <TableCell className="text-right">{Number(test.pass_mark_percent)}%</TableCell>
                  <TableCell>
                    <Button size="sm" onClick={() => setAssigning(test)} data-testid="lms-assign-open">
                      Assign
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CuratorGate>

      <AssignTestDialog
        token={token ?? ""}
        organisationId={orgId}
        test={assigning}
        onClose={() => setAssigning(null)}
      />
    </div>
  );
}
